/** Permanent materials; task state never changes the coat or eyes. */
export const danteMaterials = {
  ivory: "#E6DDC8",
  russet: "#A6673F",
  saddle: "#383A32",
  eyes: "#4E3B29",
  ink: "#252925",
  muzzle: "#C4AF91",
  tab: "#A65038",
} as const;

export const dantePalettes = {
  field: {
    background: "#EEE7D7",
    annotation: "#292B28",
    harness: "#344B59",
    harnessHighlight: "#627480",
    leather: "#876347",
    brass: "#A58E57",
    map: "#A7A08E",
    observing: "#536C75",
    uncertain: "#826C3F",
    complete: "#526C55",
    waiting: "#9B503B",
    error: "#8B4238",
  },
  watch: {
    background: "#18232B",
    annotation: "#D7DCD5",
    harness: "#293F49",
    harnessHighlight: "#657C85",
    leather: "#695440",
    brass: "#968466",
    map: "#71838B",
    observing: "#9FB7BE",
    uncertain: "#C7B17A",
    complete: "#9EB399",
    waiting: "#D99372",
    error: "#D68E83",
  },
  dispatch: {
    background: "#F1EADF",
    annotation: "#303832",
    harness: "#37463F",
    harnessHighlight: "#6A786D",
    leather: "#725A45",
    brass: "#8A805D",
    map: "#AAA796",
    observing: "#526B63",
    uncertain: "#7E6B45",
    complete: "#516A58",
    waiting: "#9B503B",
    error: "#884439",
  },
} as const;

export type DantePaletteColors = { -readonly [K in keyof typeof dantePalettes.field]: string };

/** Composite equipment and environment in linear RGB, then encode the result in sRGB. */
export function blendDantePalette(
  from: DantePaletteColors,
  to: DantePaletteColors,
  progress: number,
): DantePaletteColors {
  const t = Math.max(0, Math.min(1, progress));
  const linear = (value: number) =>
    value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  const srgb = (value: number) =>
    value <= 0.0031308 ? value * 12.92 : 1.055 * value ** (1 / 2.4) - 0.055;
  const result = {} as DantePaletteColors;
  for (const key of Object.keys(from) as (keyof DantePaletteColors)[]) {
    result[key] = `#${[1, 3, 5]
      .map((offset) => {
        const a = linear(Number.parseInt(from[key].slice(offset, offset + 2), 16) / 255);
        const b = linear(Number.parseInt(to[key].slice(offset, offset + 2), 16) / 255);
        return Math.round(srgb(a + (b - a) * t) * 255)
          .toString(16)
          .padStart(2, "0");
      })
      .join("")}`;
  }
  return result;
}

export function renderDanteTokensCss(): string {
  const variables = (values: Record<string, string>) =>
    Object.entries(values)
      .map(
        ([name, value]) =>
          `  --dante-${name.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)}: ${value.toLowerCase()};`,
      )
      .join("\n");
  return [
    `:root {\n${variables(danteMaterials)}\n}`,
    ...Object.entries(dantePalettes).map(([name, palette]) => {
      const inherited =
        name === "watch"
          ? ':root, [data-theme="dark"], '
          : name === "field"
            ? '[data-theme="light"], '
            : "";
      return `${inherited}[data-dante-palette="${name}"] {\n${variables(palette)}\n}`;
    }),
  ].join("\n\n");
}

export const APPEARANCE_PREFERENCES = ["system", "light", "dark"] as const;

export type AppearancePreference = (typeof APPEARANCE_PREFERENCES)[number];

export type ResolvedAppearance = "light" | "dark";

export const UI_APPEARANCE_STORAGE_KEY = "dante.uiAppearance";

/**
 * Semantic palette shared by web, Electron, and Expo. Names follow the shadcn
 * convention so the same slot means the same thing on every surface: a `border`
 * is always a border and never a fill.
 */
export type ColorTokens = {
  background: string;
  /** Modals: a step lighter than the page so they read as raised above it. */
  dialog: string;
  foreground: string;
  card: string;
  cardForeground: string;
  popover: string;
  popoverForeground: string;
  /** Fill and hairline where native Liquid Glass is unavailable (Android, iOS before 26). */
  glassFill: string;
  glassStroke: string;
  primary: string;
  primaryForeground: string;
  secondary: string;
  secondaryForeground: string;
  chatUser: string;
  /** Contrasting annotation ink on the harness-colored user bubble. */
  chatUserForeground: string;
  muted: string;
  mutedForeground: string;
  /** Tertiary text: placeholders, list markers, quote bars, timestamps. */
  faintForeground: string;
  accent: string;
  accentForeground: string;
  /** Destructive text and outlines. */
  destructive: string;
  /** Solid destructive fill (Hang up, a filled Delete); its text is `destructiveForeground`. */
  destructiveFill: string;
  destructiveForeground: string;
  border: string;
  input: string;
  ring: string;
  sidebar: string;
  sidebarForeground: string;
  sidebarBorder: string;
  sidebarAccent: string;
  sidebarAccentForeground: string;
  link: string;
  /** Backing behind a link's site icon. White in both themes so dark logos stay visible on any bubble. */
  faviconPlate: string;
  success: string;
  warning: string;
  statusWorking: string;
  /** Unread indicators use the observing accent. */
  unread: string;
  unreadForeground: string;
  /** Working indicators use the observing accent, distinct from completed results. */
  workingDot: string;
  overlay: string;
  scrollbar: string;
  scrollbarHover: string;
};

export const darkTokens = {
  background: dantePalettes.watch.background,
  dialog: "#22313A",
  foreground: dantePalettes.watch.annotation,
  card: "#24323A",
  cardForeground: dantePalettes.watch.annotation,
  popover: "#24323A",
  popoverForeground: dantePalettes.watch.annotation,
  glassFill: "#24323A",
  glassStroke: `${dantePalettes.watch.annotation}26`,
  primary: dantePalettes.watch.annotation,
  primaryForeground: dantePalettes.watch.background,
  secondary: dantePalettes.watch.harness,
  secondaryForeground: dantePalettes.watch.annotation,
  chatUser: dantePalettes.field.harness,
  chatUserForeground: dantePalettes.watch.annotation,
  muted: "#202D35",
  mutedForeground: "#BAC3C2",
  faintForeground: "#89999D",
  accent: `${dantePalettes.watch.harness}80`,
  accentForeground: dantePalettes.watch.annotation,
  destructive: dantePalettes.watch.error,
  destructiveFill: dantePalettes.field.error,
  destructiveForeground: dantePalettes.field.background,
  border: `${dantePalettes.watch.map}40`,
  input: `${dantePalettes.watch.map}80`,
  ring: dantePalettes.watch.observing,
  sidebar: "#1C2932",
  sidebarForeground: dantePalettes.watch.annotation,
  sidebarBorder: `${dantePalettes.watch.map}40`,
  sidebarAccent: "#2B3A43",
  sidebarAccentForeground: dantePalettes.watch.annotation,
  link: dantePalettes.watch.annotation,
  faviconPlate: "#FFFFFF",
  success: dantePalettes.watch.complete,
  warning: dantePalettes.watch.uncertain,
  statusWorking: dantePalettes.watch.observing,
  unread: dantePalettes.watch.observing,
  unreadForeground: dantePalettes.watch.background,
  workingDot: dantePalettes.watch.observing,
  overlay: "rgba(0, 0, 0, 0.28)",
  scrollbar: dantePalettes.watch.map,
  scrollbarHover: dantePalettes.watch.observing,
} as const satisfies ColorTokens;

export const lightTokens = {
  background: dantePalettes.field.background,
  dialog: "#F9F5EC",
  foreground: dantePalettes.field.annotation,
  card: "#FAF7F0",
  cardForeground: dantePalettes.field.annotation,
  popover: "#FAF7F0",
  popoverForeground: dantePalettes.field.annotation,
  glassFill: "#FAF7F0",
  glassStroke: `${dantePalettes.field.annotation}26`,
  primary: dantePalettes.field.annotation,
  primaryForeground: dantePalettes.field.background,
  secondary: dantePalettes.field.background,
  secondaryForeground: dantePalettes.field.annotation,
  chatUser: dantePalettes.field.harness,
  chatUserForeground: dantePalettes.field.background,
  muted: "#E4DDCD",
  mutedForeground: "#595A50",
  faintForeground: "#757568",
  accent: `${dantePalettes.field.harness}14`,
  accentForeground: dantePalettes.field.annotation,
  destructive: dantePalettes.field.error,
  destructiveFill: dantePalettes.field.error,
  destructiveForeground: dantePalettes.field.background,
  border: `${dantePalettes.field.map}60`,
  input: `${dantePalettes.field.map}80`,
  ring: dantePalettes.field.observing,
  sidebar: "#E5DECE",
  sidebarForeground: dantePalettes.field.annotation,
  sidebarBorder: `${dantePalettes.field.map}60`,
  sidebarAccent: "#F9F5EC",
  sidebarAccentForeground: dantePalettes.field.annotation,
  link: dantePalettes.field.annotation,
  faviconPlate: "#FFFFFF",
  success: dantePalettes.field.complete,
  warning: dantePalettes.field.uncertain,
  statusWorking: dantePalettes.field.observing,
  unread: dantePalettes.field.observing,
  unreadForeground: dantePalettes.field.background,
  workingDot: dantePalettes.field.observing,
  overlay: "rgba(0, 0, 0, 0.19)",
  scrollbar: dantePalettes.field.map,
  scrollbarHover: dantePalettes.field.observing,
} as const satisfies ColorTokens;

/** Dark palette. Prefer `tokensForAppearance` when theme-aware. */
export const tokens = darkTokens;

export const RADIUS = "0.75rem";

export const fontWeight = { regular: "400", medium: "500", semibold: "600" } as const;

/**
 * Native text styles on the system font, in points: iOS body text is 17/22. Titles use the
 * font's own line spacing, written out so they don't inherit a smaller body line height.
 */
export const typeScale = {
  title: { fontSize: 22, lineHeight: 26, fontWeight: fontWeight.semibold },
  headline: { fontSize: 19, lineHeight: 23, fontWeight: fontWeight.semibold },
  cardTitle: { fontSize: 17, lineHeight: 22, fontWeight: fontWeight.medium },
  body: { fontSize: 17, lineHeight: 22 },
  secondary: { fontSize: 15, lineHeight: 19 },
  caption: { fontSize: 14, lineHeight: 18 },
  footnote: { fontSize: 12, lineHeight: 16 },
} as const;

/**
 * Text grows with the iOS text size until body text reaches 23pt, the xxxLarge size. Larger
 * accessibility sizes keep that size, so message bubbles, rows and cards keep their layout.
 */
export const MAX_FONT_SCALE = 23 / typeScale.body.fontSize;

export const botColors = [
  "#3EC5A8",
  "#F5A03C",
  "#6A6BF5",
  "#9B5CF6",
  "#3B82F6",
  "#F2622A",
  "#D9508A",
] as const;

export function isAppearancePreference(
  value: string | null | undefined,
): value is AppearancePreference {
  return value === "system" || value === "light" || value === "dark";
}

export function normalizeAppearancePreference(
  raw: string | null | undefined,
): AppearancePreference {
  return isAppearancePreference(raw) ? raw : "system";
}

export type ResolveAppearancePreferenceOptions = {
  stored?: string | null;
  storage?: Pick<Storage, "getItem"> | null;
};

function getLocalStorage(): Pick<Storage, "getItem" | "setItem"> | null {
  try {
    return typeof localStorage !== "undefined" ? localStorage : null;
  } catch {
    return null;
  }
}

export function resolveAppearancePreference(
  options: ResolveAppearancePreferenceOptions = {},
): AppearancePreference {
  const stored =
    options.stored !== undefined
      ? options.stored
      : readStoredAppearance(options.storage ?? getLocalStorage());
  return normalizeAppearancePreference(stored);
}

export function persistAppearancePreference(
  preference: AppearancePreference,
  storage: Pick<Storage, "setItem"> | null = getLocalStorage(),
): void {
  if (!storage) return;
  try {
    storage.setItem(UI_APPEARANCE_STORAGE_KEY, preference);
  } catch {
    // Ignore quota / private-mode failures; in-memory preference still applies.
  }
}

export function resolveAppearance(
  preference: AppearancePreference,
  system: ResolvedAppearance = "dark",
): ResolvedAppearance {
  if (preference === "system") return system;
  return preference;
}

export function tokensForAppearance(appearance: ResolvedAppearance): ColorTokens {
  return appearance === "light" ? lightTokens : darkTokens;
}

function readStoredAppearance(storage: Pick<Storage, "getItem"> | null | undefined): string | null {
  if (!storage) return null;
  try {
    return storage.getItem(UI_APPEARANCE_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** `cardForeground` -> `--card-foreground` */
export function cssVariableName(token: keyof ColorTokens): string {
  return `--${token.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)}`;
}

function renderBlock(selector: string, colorScheme: ResolvedAppearance, palette: ColorTokens) {
  const lines = (Object.keys(palette) as (keyof ColorTokens)[]).map(
    (token) => `  ${cssVariableName(token)}: ${palette[token].toLowerCase()};`,
  );
  return `${selector} {\n  color-scheme: ${colorScheme};\n${lines.join("\n")}\n  --radius: ${RADIUS};\n}`;
}

/** The CSS in `tokens.css`. Generated from the TS palette so both stay in sync. */
export function renderTokensCss(): string {
  return `${[
    "/* Generated by `pnpm --filter @dante/ui-tokens generate`. Edit src/index.ts instead. */",
    renderBlock(':root,\n[data-theme="dark"]', "dark", darkTokens),
    renderBlock('[data-theme="light"]', "light", lightTokens),
    renderDanteTokensCss(),
  ].join("\n\n")}\n`;
}
